import React, { useCallback, useEffect } from 'react'
import { FlatList } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useToast } from '@zet/common/components/toast'
import { IRequestData } from '@zet/customer/src/api/functions/customerAdditionStateMachine'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Stack } from '@zet/design-system'
import { i18n } from '@zet/localization'
import { useEditBasicDetails } from '@zet/zet-pro-quick-journey/hooks/useEditBasicDetails'
import { useForm } from 'react-hook-form'

import { Button, ScreenTitle } from '../../components'
import FormController from '../../form/FormController'
import { useGetPrefilledData } from '../../hooks/useGetPrefillCustomerData'
import { sendAnalyticsData } from '../../utils/analyticsEvents'
import { customerBasicDetailsValidationSchema } from '../../utils/validationSchemas'
import { editBasicDetailsFormFields } from './formFields'

export const EditCustomerBasicDetails = ({
  refetchRecommendation,
  setShowEditBasicDetailsSheet,
}: {
  refetchRecommendation: () => void
  setShowEditBasicDetailsSheet: (arg: boolean) => void
}) => {
  const { mutate, isLoading } = useEditBasicDetails()
  const { hashAfterOtp } = useAddCustomerStore()
  const urlParams = new URLSearchParams(window.location.search)
  const customerId = parseInt(urlParams.get('customerId') ?? '')
  const { data } = useGetPrefilledData({
    customerId: customerId,
    hash: hashAfterOtp,
  })

  useEffect(() => {
    setValue('pincode', data?.pincode)
    setValue('employmentType', data?.employmentType)
    setValue('annualIncome', data?.annualIncome)
    setValue(
      'modeOfIncome',
      data?.modeOfIncome?.toLowerCase() == 'bank' ? 'Bank' : 'Cash',
    )
  }, [data])

  const {
    control,
    formState: { isValid },
    getValues,
    setValue,
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(customerBasicDetailsValidationSchema),
  })

  const toast = useToast()

  useEffect(() => {
    sendAnalyticsData({
      screen: 'EDIT_BASIC_DETAILS_SCREEN_LOAD',
      type: 'screen',
    })
  }, [])

  const renderItem = useCallback(({ item }: any) => {
    return <FormController control={control} ocField={item} />
  }, [])

  const handleSubmit = () => {
    const { employmentType, pincode, annualIncome, modeOfIncome } = getValues()
    const urlParams = new URLSearchParams(window.location.search)

    const custId = urlParams.get('customerId')

    const monthlyIncome = annualIncome.replace(/,/g, '')
    const requestBodyData: IRequestData = {
      employmentType,
      pinCode: pincode,
      monthlyIncome,
      modeOfIncome,
      customerId: customerId,
    }
    mutate(requestBodyData, {
      onSuccess: () => {
        setShowEditBasicDetailsSheet(false)
        refetchRecommendation()
      },
      onError: () => {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: i18n.t('toastMessages.somethingWentWrong'),
          hideAfter: 3000,
        })
      },
    })
  }

  const closeModal = () => {
    setShowEditBasicDetailsSheet(false)
  }

  return (
    <Stack className="bg-white">
      <Stack className="bg-white px-3">
        <FlatList
          showsVerticalScrollIndicator={false}
          data={editBasicDetailsFormFields}
          ListHeaderComponent={
            <ScreenTitle
              textStyle="text-start"
              className="mt-1"
              innerContainerClassName="pl-0"
              title={'Edit your info'}
              onCrossPress={closeModal}
            />
          }
          renderItem={renderItem}
        />
      </Stack>
      <Stack>
        <Button
          disabled={!isValid || isLoading}
          onPress={handleSubmit}
          color="bg-secondary-100"
          title={'Submit'}
          loading={isLoading}
          loaderText="Verifying"
          textColor="text-black"
        />
      </Stack>
    </Stack>
  )
}
